/**
 * NewContractPage
 *
 * Page for creating a new contract.
 * Step-by-step wizard: Select Client → Select Template → Customize → Review & Send
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Alert,
  Skeleton,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Description as TemplateIcon,
  Add as AddIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { contractTemplatesApi, contractsApi, ContractTemplateListItem, CustomField } from '../../services/contractsApi';
import { api, getErrorMessage } from '../../services/api';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
}

const STEPS = ['Select Client', 'Choose Template', 'Customize', 'Review & Send'];

export default function NewContractPage() {
  const navigate = useNavigate();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplateListItem | null>(null);
  const [contractName, setContractName] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('Please review and sign this contract.');
  const [error, setError] = useState<string | null>(null);

  // Search states
  const [clientSearch, setClientSearch] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');

  // Fetch clients
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients', clientSearch],
    queryFn: async () => {
      const response = await api.get('/clients', {
        params: { search: clientSearch || undefined, limit: 50 },
      });
      return response.data.data;
    },
  });

  // Fetch templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['contract-templates', templateSearch],
    queryFn: () => contractTemplatesApi.list({ search: templateSearch || undefined }),
    enabled: activeStep >= 1,
  });

  // Fetch full template when selected
  const { data: fullTemplate } = useQuery({
    queryKey: ['contract-template', selectedTemplate?._id],
    queryFn: () => contractTemplatesApi.get(selectedTemplate!._id),
    enabled: Boolean(selectedTemplate?._id),
  });

  // Create and send contract mutation
  const createAndSendMutation = useMutation({
    mutationFn: async () => {
      // Create the contract
      const contract = await contractsApi.create({
        clientId: selectedClient!._id,
        templateId: selectedTemplate?._id,
        name: contractName,
        customFieldValues,
      });

      // Send it for signature
      await contractsApi.send(contract._id, { message });

      return contract;
    },
    onSuccess: (contract) => {
      navigate(`/contracts/${contract._id}?sent=true`);
    },
    onError: (err) => {
      setError(getErrorMessage(err));
    },
  });

  // Save as draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      return await contractsApi.create({
        clientId: selectedClient!._id,
        templateId: selectedTemplate?._id,
        name: contractName,
        customFieldValues,
      });
    },
    onSuccess: (contract) => {
      navigate(`/contracts/${contract._id}`);
    },
    onError: (err) => {
      setError(getErrorMessage(err));
    },
  });

  // Set default contract name when template and client are selected
  useEffect(() => {
    if (selectedClient && selectedTemplate && !contractName) {
      setContractName(`${selectedTemplate.name} - ${selectedClient.firstName} ${selectedClient.lastName}`);
    }
  }, [selectedClient, selectedTemplate, contractName]);

  // Initialize custom field values when template changes
  useEffect(() => {
    if (fullTemplate?.customFields) {
      const values: Record<string, string> = {};
      fullTemplate.customFields.forEach((field: CustomField) => {
        values[field.name] = field.defaultValue || '';
      });
      setCustomFieldValues(values);
    }
  }, [fullTemplate]);

  // Navigation handlers
  const handleNext = () => {
    setError(null);
    if (activeStep === 0 && !selectedClient) {
      setError('Please select a client');
      return;
    }
    if (activeStep === 1 && !selectedTemplate) {
      setError('Please select a template');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => prev - 1);
  };

  const handleSendContract = () => {
    setError(null);
    if (!contractName.trim()) {
      setError('Contract name is required');
      return;
    }
    createAndSendMutation.mutate();
  };

  const handleSaveDraft = () => {
    setError(null);
    if (!contractName.trim()) {
      setError('Contract name is required');
      return;
    }
    saveDraftMutation.mutate();
  };

  const clients = clientsData?.clients || [];
  const templates = templatesData?.templates || [];
  const isSending = createAndSendMutation.isPending || saveDraftMutation.isPending;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/contracts')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          New Contract
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Select Client */}
      {activeStep === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select a Client
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose the client who will receive this contract.
          </Typography>

          <TextField
            fullWidth
            placeholder="Search clients..."
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {clientsLoading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : clients.length === 0 ? (
            <Alert severity="info">
              {clientSearch ? `No clients found matching "${clientSearch}"` : 'No clients found. Create a client first.'}
            </Alert>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {clients.map((client: Client) => (
                <ListItem key={client._id} disablePadding>
                  <ListItemButton
                    selected={selectedClient?._id === client._id}
                    onClick={() => setSelectedClient(client)}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${client.firstName} ${client.lastName}`}
                      secondary={
                        <>
                          {client.email}
                          {client.company && ` • ${client.company}`}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              disabled={!selectedClient}
            >
              Next
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 2: Choose Template */}
      {activeStep === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Choose a Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a template to use for this contract.
          </Typography>

          <TextField
            fullWidth
            placeholder="Search templates..."
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {templatesLoading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : templates.length === 0 ? (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/contracts/templates/new')}
                >
                  Create Template
                </Button>
              }
            >
              {templateSearch
                ? `No templates found matching "${templateSearch}"`
                : 'No templates found. Create a template first.'}
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template._id}>
                  <Card
                    sx={{
                      border: selectedTemplate?._id === template._id ? 2 : 1,
                      borderColor: selectedTemplate?._id === template._id ? 'primary.main' : 'divider',
                    }}
                  >
                    <CardActionArea onClick={() => setSelectedTemplate(template)}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <TemplateIcon color="action" />
                          <Typography variant="subtitle1" fontWeight={500}>
                            {template.name}
                          </Typography>
                        </Box>
                        {template.description && (
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button startIcon={<BackIcon />} onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
              disabled={!selectedTemplate}
            >
              Next
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 3: Customize */}
      {activeStep === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Customize Contract
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fill in any custom fields for this contract.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contract Name"
                value={contractName}
                onChange={(e) => setContractName(e.target.value)}
                required
              />
            </Grid>

            {fullTemplate?.customFields && fullTemplate.customFields.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Custom Fields
                  </Typography>
                </Grid>
                {fullTemplate.customFields.map((field: CustomField) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <TextField
                      fullWidth
                      label={field.label}
                      value={customFieldValues[field.name] || ''}
                      onChange={(e) =>
                        setCustomFieldValues((prev) => ({
                          ...prev,
                          [field.name]: e.target.value,
                        }))
                      }
                      required={field.required}
                      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button startIcon={<BackIcon />} onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={<NextIcon />}
              onClick={handleNext}
            >
              Next
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 4: Review & Send */}
      {activeStep === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Review & Send
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Review the contract details and send for signature.
          </Typography>

          {/* Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Client
            </Typography>
            <Typography variant="body1">
              {selectedClient?.firstName} {selectedClient?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedClient?.email}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Template
            </Typography>
            <Typography variant="body1">
              {selectedTemplate?.name}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Contract Name
            </Typography>
            <Typography variant="body1">
              {contractName}
            </Typography>
          </Box>

          {Object.keys(customFieldValues).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Custom Field Values
              </Typography>
              {Object.entries(customFieldValues).map(([key, value]) => (
                <Typography key={key} variant="body2">
                  <strong>{key}:</strong> {value || '(empty)'}
                </Typography>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <TextField
            fullWidth
            label="Message to Client"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            rows={3}
            helperText="This message will be included in the email sent to the client"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button startIcon={<BackIcon />} onClick={handleBack} disabled={isSending}>
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                disabled={isSending}
              >
                Save as Draft
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendContract}
                disabled={isSending}
              >
                {isSending ? 'Sending...' : 'Send for Signature'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
